import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";

export const PLATFORMS = [
  { id: "zillow", name: "Zillow", group: "Zillow Group" },
  { id: "trulia", name: "Trulia", group: "Zillow Group" },
  { id: "hotpads", name: "HotPads", group: "Zillow Group" },
  { id: "apartments_com", name: "Apartments.com", group: "CoStar Group" },
  { id: "rent_com", name: "Rent.com", group: "CoStar Group" },
  { id: "realtor_com", name: "Realtor.com", group: "Move/News Corp" },
  { id: "facebook", name: "Facebook Marketplace", group: "Social" },
  { id: "craigslist", name: "Craigslist", group: "Classifieds" },
] as const;

export type PlatformId = typeof PLATFORMS[number]["id"];

export interface RentalListing {
  id: string;
  workspace_id: string;
  pipeline_property_id: string;
  created_by_user_id: string;
  status: "draft" | "active" | "paused" | "rented" | "archived";
  title: string;
  description: string | null;
  rent_amount: number;
  security_deposit: number | null;
  lease_term_months: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  available_date: string | null;
  pet_policy: string | null;
  utilities_included: string[] | null;
  amenities: string[] | null;
  photos: string[] | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  template_id: string | null;
  /** Screening criteria (step 4). Credit/income are null for Section 8 listings. */
  minimum_credit_score: number | null;
  income_to_rent_ratio: number | null;
  background_check: boolean;
  accept_section8_voucher: boolean;
  created_at: string;
  updated_at: string;
}

export interface RentalSyndication {
  id: string;
  workspace_id: string;
  listing_id: string;
  platform: string;
  status: "pending" | "published" | "failed" | "removed";
  external_url: string | null;
  published_at: string | null;
  last_synced_at: string | null;
  error_message: string | null;
  created_at: string;
}

export interface RentalLead {
  id: string;
  workspace_id: string;
  /** NULL once the listing was deleted — such rows are `status: "archived"`. */
  listing_id: string | null;
  /**
   * The property this application was made against. Outlives the listing, so
   * Applicant History can still name what the applicant applied for.
   */
  pipeline_property_id: string | null;
  platform: string | null;
  status: "new" | "contacted" | "showing_scheduled" | "application_sent" | "approved" | "denied" | "lost" | "archived";
  name: string;
  email: string | null;
  phone: string | null;
  message: string | null;
  showing_date: string | null;
  notes: string | null;
  first_contacted_at: string | null;
  created_at: string;
}

/**
 * Whether a listing may be permanently deleted.
 *
 * Only a **rented** listing is blocked — it's the record of a live tenancy, so
 * it has to come off rent first ("Remove from rent" declines the approved
 * applicant and flips it back to `active`).
 *
 * Applications do NOT block deletion: `rental_leads.listing_id` is ON DELETE SET
 * NULL as of migration 20260814000100, and a trigger flips those rows to
 * `archived` so they survive in Applicant History.
 *
 * Pure for testing.
 */
export function isListingDeletable(
  listing: Pick<RentalListing, "status"> | null | undefined,
): boolean {
  return !!listing && listing.status !== "rented";
}

/**
 * Applications still attached to a live listing — the Applications tab.
 * Pure for testing.
 */
export function filterActiveApplications(leads: RentalLead[]): RentalLead[] {
  return leads.filter((l) => l.status !== "archived");
}

/**
 * Applications whose listing has been deleted — the Applicant History tab. They
 * keep `pipeline_property_id`, so the property is still knowable even though
 * `listing_id` is NULL.
 * Pure for testing.
 */
export function filterApplicantHistory(leads: RentalLead[]): RentalLead[] {
  return leads.filter((l) => l.status === "archived");
}

/**
 * The application already approved on a listing — the one holding the unit.
 * One unit can only be let to one applicant, so a second approval has to be
 * refused rather than quietly leaving two people "approved" for the same door.
 *
 * `exceptLeadId` skips the application being updated: re-approving whoever
 * already holds the listing is a no-op, not a conflict.
 * Pure for testing.
 */
export function findApprovedApplicant(
  leads: RentalLead[],
  listingId: string | null | undefined,
  exceptLeadId?: string,
): RentalLead | null {
  if (!listingId) return null;
  return (
    leads.find(
      (l) =>
        l.id !== exceptLeadId &&
        l.listing_id === listingId &&
        l.status === "approved",
    ) ?? null
  );
}

export interface RentalTemplate {
  id: string;
  workspace_id: string;
  name: string;
  description_template: string | null;
  default_rent: number | null;
  default_deposit: number | null;
  default_lease_term: number | null;
  default_pet_policy: string | null;
  default_utilities: string[] | null;
  default_amenities: string[] | null;
}

export function useRentals() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  // Listings
  const { data: listings = [], isLoading: listingsLoading } = useQuery({
    queryKey: ["rental-listings", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("rental_listings")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as RentalListing[];
    },
    enabled: !!workspaceId,
  });

  // Syndications
  const { data: syndications = [], isLoading: syndicationsLoading } = useQuery({
    queryKey: ["rental-syndications", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("rental_syndications")
        .select("*")
        .eq("workspace_id", workspaceId);
      if (error) throw error;
      return data as unknown as RentalSyndication[];
    },
    enabled: !!workspaceId,
  });

  // Leads
  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ["rental-leads", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("rental_leads")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as RentalLead[];
    },
    enabled: !!workspaceId,
  });

  // Templates
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["rental-templates", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("rental_templates")
        .select("*")
        .eq("workspace_id", workspaceId);
      if (error) throw error;
      return data as unknown as RentalTemplate[];
    },
    enabled: !!workspaceId,
  });

  // Create listing
  const createListing = useMutation({
    mutationFn: async (listing: Omit<RentalListing, "id" | "workspace_id" | "created_by_user_id" | "created_at" | "updated_at">) => {
      if (!workspaceId || !user) throw new Error("No workspace or user");
      const { error } = await supabase.from("rental_listings").insert({
        ...listing,
        workspace_id: workspaceId,
        created_by_user_id: user.id,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rental-listings", workspaceId] });
      toast.success("Listing created");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to create listing")),
  });

  // Update listing — triggers Zillow sync when status changes
  const updateListing = useMutation({
    mutationFn: async ({ id, ...data }: Partial<RentalListing> & { id: string }) => {
      const { data: rows, error } = await supabase.from("rental_listings").update(data as never).eq("id", id).select("id");
      if (error) throw error;
      assertRowsAffected(rows, "update this rental listing");

      // When a listing is deactivated, signal Zillow to delist it immediately
      const delistStatuses = ["paused", "archived", "rented"];
      if (data.status && delistStatuses.includes(data.status)) {
        // Check if this listing has active Zillow syndication
        const { data: syns } = await supabase
          .from("rental_syndications")
          .select("id")
          .eq("listing_id", id)
          .in("platform", ["zillow", "trulia", "hotpads"])
          .eq("status", "published")
          .limit(1);

        if (syns && syns.length > 0) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            await supabase.functions.invoke("zillow-sync", {
              body: { listingIds: [id], action: "unpublish", workspaceId },
              headers: session?.access_token
                ? { Authorization: `Bearer ${session.access_token}` }
                : {},
            });
          } catch (e) {
            console.warn("zillow-sync delist error:", e);
          }
        }
      }

      // When re-activated, re-sync to Zillow
      if (data.status === "active") {
        const { data: syns } = await supabase
          .from("rental_syndications")
          .select("id")
          .eq("listing_id", id)
          .in("platform", ["zillow", "trulia", "hotpads"])
          .limit(1);

        if (syns && syns.length > 0) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            await supabase.functions.invoke("zillow-sync", {
              body: { listingIds: [id], action: "publish", workspaceId },
              headers: session?.access_token
                ? { Authorization: `Bearer ${session.access_token}` }
                : {},
            });
          } catch (e) {
            console.warn("zillow-sync re-publish error:", e);
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rental-listings", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["rental-syndications", workspaceId] });
      toast.success("Listing updated");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update listing")),
  });

  // Delete listing — permanent, and scoped to the `rental_listings` row itself.
  // Rented units are refused (see isListingDeletable) so a live tenancy can't be
  // erased by accident.
  //
  // Two DB-side consequences, both handled by migration 20260814000100 rather
  // than here, so they hold for any caller:
  //   - rental_leads      → listing_id NULL + status 'archived' (kept as history)
  //   - rental_syndications → cascade-deleted (our own publish bookkeeping)
  //
  // Deliberately does NOT delist from the platforms: no zillow-sync unpublish.
  // A listing that was live on Zillow keeps showing there until the feed drops
  // it. That cleanup lands separately.
  const deleteListing = useMutation({
    mutationFn: async (id: string) => {
      const listing = listings.find((l) => l.id === id);
      if (listing && !isListingDeletable(listing)) {
        throw new Error(
          "This unit is rented — remove it from rent before deleting the listing.",
        );
      }

      const { data: rows, error } = await supabase
        .from("rental_listings")
        .delete()
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "delete this rental listing");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rental-listings", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["rental-syndications", workspaceId] });
      // The listing's applications were just archived and detached — refetch so
      // they move from Applications to Applicant History.
      queryClient.invalidateQueries({ queryKey: ["rental-leads", workspaceId] });
      toast.success("Listing deleted");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to delete listing")),
  });

  // Publish to platforms + trigger Zillow real-time sync if applicable
  const publishToPlatforms = useMutation({
    mutationFn: async ({ listingId, platforms }: { listingId: string; platforms: string[] }) => {
      if (!workspaceId) throw new Error("No workspace");
      const rows = platforms.map((platform) => ({
        workspace_id: workspaceId,
        listing_id: listingId,
        platform,
        status: "pending" as const,           // pending until Zillow confirms
        published_at: new Date().toISOString(),
      }));
      const { error } = await supabase.from("rental_syndications").insert(rows as never);
      if (error) throw error;

      // Trigger Zillow real-time markForUpdate for feed-based platforms
      const feedPlatforms = ["zillow", "trulia", "hotpads"];
      const needsZillowSync = platforms.some((p) => feedPlatforms.includes(p));
      if (needsZillowSync) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const res = await supabase.functions.invoke("zillow-sync", {
            body: { listingIds: [listingId], action: "publish", workspaceId },
            headers: session?.access_token
              ? { Authorization: `Bearer ${session.access_token}` }
              : {},
          });
          if (res.error) console.warn("zillow-sync failed:", res.error);
        } catch (e) {
          console.warn("zillow-sync invoke error:", e);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rental-syndications", workspaceId] });
      toast.success("Published to platforms — Zillow will reflect changes within minutes");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to publish")),
  });

  // Update syndication status
  const updateSyndication = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data, error } = await supabase.from("rental_syndications").update({ status } as never).eq("id", id).select("id");
      if (error) throw error;
      assertRowsAffected(data, "update this syndication");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rental-syndications", workspaceId] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update syndication")),
  });

  // Create lead. `pipeline_property_id` is derived from the listing rather than
  // asked of callers — it's the link that has to survive the listing being
  // deleted, so it can't depend on every insert site remembering it.
  const createLead = useMutation({
    mutationFn: async (
      lead: Omit<RentalLead, "id" | "workspace_id" | "created_at" | "pipeline_property_id">,
    ) => {
      if (!workspaceId || !user) throw new Error("No workspace or user");
      const forListing = listings.find((l) => l.id === lead.listing_id);
      const { error } = await supabase.from("rental_leads").insert({
        ...lead,
        pipeline_property_id: forListing?.pipeline_property_id ?? null,
        workspace_id: workspaceId,
      } as never);
      if (error) throw error;

      // Create in-app notification
      const listing = forListing;
      const platformName = lead.platform
        ? PLATFORMS.find((p) => p.id === lead.platform)?.name || lead.platform
        : "Direct";
      await supabase.from("notifications").insert({
        workspace_id: workspaceId,
        user_id: user.id,
        type: "lead",
        title: `New lead from ${platformName}`,
        body: `${lead.name} inquired about "${listing?.title || "a listing"}"`,
        entity_type: "rental_lead",
        entity_id: lead.listing_id,
      } as never);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rental-leads", workspaceId] });
      toast.success("Lead added");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to add lead")),
  });

  // Update lead
  const updateLead = useMutation({
    mutationFn: async ({ id, ...data }: Partial<RentalLead> & { id: string }) => {
      // One unit, one tenant. Approving marks the listing "rented" below, so a
      // second approval would hand the same door to two applicants. Refused
      // here rather than only in the pickers, so every caller is covered.
      if (data.status === "approved") {
        const current = leads.find((l) => l.id === id);
        const holder = findApprovedApplicant(leads, current?.listing_id, id);
        if (holder) {
          throw new Error(
            `${holder.name} is already approved for this listing. Move them off Approved before approving someone else.`,
          );
        }
      }

      const { data: rows, error } = await supabase.from("rental_leads").update(data as never).eq("id", id).select("id");
      if (error) throw error;
      assertRowsAffected(rows, "update this lead");

      // Keep the listing's status in sync with this application:
      //   - Approving it means the unit is taken → mark the listing "rented"
      //     (drops out of the Add-Lead picker; updateListing also delists from
      //     Zillow).
      //   - Moving it OUT of "approved" frees the unit again → mark the listing
      //     back to "active".
      // Reuse updateListing so the side-effects + cache invalidation live in
      // one place. `lead` here is the pre-update row, so its status is the old
      // value we compare against.
      if (data.status) {
        const lead = leads.find((l) => l.id === id);
        const listing = listings.find((l) => l.id === lead?.listing_id);
        if (listing) {
          const wasApproved = lead?.status === "approved";
          const nowApproved = data.status === "approved";
          if (nowApproved && listing.status !== "rented") {
            await updateListing.mutateAsync({ id: listing.id, status: "rented" });
          } else if (wasApproved && !nowApproved && listing.status === "rented") {
            await updateListing.mutateAsync({ id: listing.id, status: "active" });
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rental-leads", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["rental-listings", workspaceId] });
      toast.success("Lead updated");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update lead")),
  });

  // Create template
  const createTemplate = useMutation({
    mutationFn: async (template: Omit<RentalTemplate, "id" | "workspace_id">) => {
      if (!workspaceId || !user) throw new Error("No workspace or user");
      const { error } = await supabase.from("rental_templates").insert({
        ...template,
        workspace_id: workspaceId,
        created_by_user_id: user.id,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rental-templates", workspaceId] });
      toast.success("Template saved");
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to save template")),
  });

  // Applications split by whether their listing still exists. Archived ones are
  // history: they're excluded from the live Applications list and every stat, so
  // deleting a listing doesn't quietly inflate the funnel.
  const activeLeads = useMemo(() => filterActiveApplications(leads), [leads]);
  const archivedLeads = useMemo(() => filterApplicantHistory(leads), [leads]);

  // Stats
  const activeListings = listings.filter((l) => l.status === "active").length;
  const totalLeads = activeLeads.length;
  const newLeads = activeLeads.filter((l) => l.status === "new").length;
  const publishedCount = syndications.filter((s) => s.status === "published").length;

  const getSyndicationsForListing = useCallback(
    (listingId: string) => syndications.filter((s) => s.listing_id === listingId),
    [syndications]
  );

  const getLeadsForListing = useCallback(
    (listingId: string) => leads.filter((l) => l.listing_id === listingId),
    [leads]
  );

  return {
    listings,
    syndications,
    /** Every application row, archived included. Prefer the split lists below. */
    leads,
    /** Applications on a listing that still exists — the Applications tab. */
    activeLeads,
    /** Applications whose listing was deleted — the Applicant History tab. */
    archivedLeads,
    templates,
    isLoading: listingsLoading || syndicationsLoading || leadsLoading || templatesLoading,
    activeListings,
    totalLeads,
    newLeads,
    publishedCount,
    createListing,
    updateListing,
    deleteListing,
    publishToPlatforms,
    updateSyndication,
    createLead,
    updateLead,
    createTemplate,
    getSyndicationsForListing,
    getLeadsForListing,
  };
}
